import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Actor } from "@guildmaster/core/platform";
import { api, ApiError } from "./api";
import {
  plannerClasses,
  planningRoles,
  roleLabels,
  type PlanningEntry,
  type PlanningBoard,
  type BoardSummary,
  type BoardOperation,
  type EditBoard,
  type CreateBoard,
  type HistoryEntry,
  type PlanningLease,
} from "../shared/planner";
import "./planner.css";
import {
  EntryTile,
  PlannerGlyph,
  SpecIcon,
  SpecPalette,
  type PlannerDrag,
  type SpecChoice,
} from "./planner-pieces";

const endpoint = "/api/planner/boards";
const when = (at: number) =>
  new Date(at).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
const profileName = (profile: PlanningBoard["profile"]) =>
  profile === "forever" ? "WoW Forever" : "Original Classic";
const fromLocation = () =>
  window.location.pathname.match(/^\/raid-planner\/([a-f0-9-]{36})\/?$/)?.[1] ??
  null;
const manager = (actor: Actor) =>
  actor.officeAccess &&
  actor.capabilities.some((cap) => cap === "roster" || cap === "approve");
function useSharedBoard(id: string, onDenied: (error: unknown) => void) {
  const [board, setBoard] = useState<PlanningBoard | null>(null),
    [connected, setConnected] = useState(false),
    [viewers, setViewers] = useState(0);
  const [saving, setSaving] = useState(false),
    [uncertain, setUncertain] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [removedEntry, setRemovedEntry] = useState<PlanningEntry | null>(null);
  const current = useRef<PlanningBoard | null>(null),
    pending = useRef<EditBoard | null>(null),
    pendingRemoval = useRef<PlanningEntry | null>(null),
    mounted = useRef(true);
  const accept = useCallback(
    (value: PlanningBoard) => {
      if (!mounted.current || value.id !== id) return;
      if (!current.current || value.revision >= current.current.revision) {
        current.current = value;
        setBoard(value);
        setRemovedEntry((entry) =>
          entry && value.entries.some((saved) => saved.id === entry.id)
            ? null
            : entry,
        );
      }
    },
    [id],
  );
  const refresh = useCallback(async () => {
    const value = await api<PlanningBoard>(endpoint + "/" + id, {
      signal: AbortSignal.timeout(15_000),
    });
    accept(value);
  }, [id, accept]);
  const denied = useRef(onDenied);
  denied.current = onDenied;
  useEffect(() => {
    mounted.current = true;
    let stopped = false,
      socket: WebSocket | null = null,
      timer = 0,
      ping = 0,
      debounce = 0,
      renewal = 0,
      attempt = 0,
      connecting = false,
      fetching = false,
      dirty = false,
      lastPong = Date.now();
    const handleError = (err: unknown) => {
      if (stopped) return;
      if (err instanceof ApiError && [401, 403].includes(err.status)) {
        stopped = true;
        setConnected(false);
        window.clearTimeout(renewal);
        socket?.close();
        denied.current(err);
      }
      setError(
        err instanceof Error ? err.message : "Unable to open the saved board",
      );
    };
    const scheduleRenewal = (connection: WebSocket, lease: PlanningLease) => {
      if (
        stopped ||
        socket !== connection ||
        connection.readyState !== WebSocket.OPEN
      )
        return;
      if (
        !/^[a-f0-9-]{36}$/.test(lease.connectionId) ||
        !Number.isFinite(lease.expiresInMs) ||
        lease.expiresInMs <= 0 ||
        lease.expiresInMs > 60_000
      ) {
        connection.close();
        return;
      }
      window.clearTimeout(renewal);
      renewal = window.setTimeout(
        async () => {
          if (
            stopped ||
            socket !== connection ||
            connection.readyState !== WebSocket.OPEN
          )
            return;
          try {
            const next = await api<PlanningLease>(
              endpoint + "/" + id + "/events/renew",
              {
                method: "POST",
                body: JSON.stringify({ connectionId: lease.connectionId }),
                signal: AbortSignal.timeout(10_000),
              },
            );
            if (next.connectionId !== lease.connectionId)
              throw new Error("Live connection changed");
            scheduleRenewal(connection, next);
          } catch (err) {
            if (stopped || socket !== connection) return;
            if (err instanceof ApiError && [401, 403].includes(err.status))
              handleError(err);
            else {
              setConnected(false);
              connection.close();
            }
          }
        },
        Math.max(1000, Math.min(45_000, Math.floor(lease.expiresInMs * 0.75))),
      );
    };
    const reload = async () => {
      if (fetching) {
        dirty = true;
        return;
      }
      fetching = true;
      try {
        await refresh();
      } catch (err) {
        handleError(err);
      } finally {
        fetching = false;
        if (dirty && !stopped) {
          dirty = false;
          void reload();
        }
      }
    };
    const connect = async () => {
      if (stopped || connecting) return;
      connecting = true;
      try {
        await refresh();
      } catch (err) {
        connecting = false;
        handleError(err);
        if (!stopped)
          timer = window.setTimeout(
            () => void connect(),
            Math.min(30_000, 1000 * 2 ** Math.min(attempt++, 5)),
          );
        return;
      }
      if (stopped) return;
      const address = new URL(
        endpoint + "/" + id + "/events",
        window.location.origin,
      );
      address.protocol = address.protocol === "https:" ? "wss:" : "ws:";
      const connection = new WebSocket(address);
      socket = connection;
      connection.onopen = () => {
        connecting = false;
        if (!stopped && socket === connection) {
          setConnected(true);
          attempt = 0;
          lastPong = Date.now();
        }
      };
      connection.onmessage = (event) => {
        if (stopped || socket !== connection) return;
        lastPong = Date.now();
        if (event.data === "pong") return;
        try {
          const signal = JSON.parse(String(event.data)) as {
            type: string;
            revision: number;
            viewers: number;
            board?: PlanningBoard;
            connectionId: string;
            expiresInMs: number;
          };
          if (signal.type === "lease") {
            scheduleRenewal(connection, signal);
            return;
          }
          if (signal.type !== "changed") return;
          setViewers(signal.viewers);
          if (signal.board) accept(signal.board);
          else if (signal.revision > (current.current?.revision ?? 0)) {
            window.clearTimeout(debounce);
            debounce = window.setTimeout(() => void reload(), 100);
          }
        } catch {
          connection.close();
        }
      };
      connection.onclose = (event) => {
        if (stopped || socket !== connection) return;
        connecting = false;
        window.clearTimeout(renewal);
        setConnected(false);
        timer = window.setTimeout(
          () => void connect(),
          event.code === 4001
            ? 100
            : Math.min(30_000, 1000 * 2 ** Math.min(attempt++, 5)),
        );
      };
    };
    void connect();
    ping = window.setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        if (Date.now() - lastPong > 45_000) socket.close();
        else socket.send("ping");
      }
    }, 20_000);
    const offline = () => {
      setConnected(false);
      window.clearTimeout(renewal);
      socket?.close();
    };
    const online = () => {
      if (!stopped && (!socket || socket.readyState === WebSocket.CLOSED)) {
        window.clearTimeout(timer);
        void connect();
      }
    };
    window.addEventListener("offline", offline);
    window.addEventListener("online", online);
    return () => {
      stopped = true;
      mounted.current = false;
      window.clearTimeout(timer);
      window.clearTimeout(debounce);
      window.clearTimeout(renewal);
      window.clearInterval(ping);
      window.removeEventListener("offline", offline);
      window.removeEventListener("online", online);
      socket?.close();
    };
  }, [id, refresh, accept]);
  async function send(input: EditBoard): Promise<boolean> {
    pending.current = input;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const value = await api<PlanningBoard>(endpoint + "/" + id, {
        method: "POST",
        body: JSON.stringify(input),
        signal: AbortSignal.timeout(15_000),
      });
      accept(value);
      if (
        pendingRemoval.current &&
        !current.current?.entries.some(
          (entry) => entry.id === pendingRemoval.current!.id,
        )
      )
        setRemovedEntry(pendingRemoval.current);
      pending.current = null;
      pendingRemoval.current = null;
      setUncertain(false);
      setNotice("Saved to the shared board.");
      return true;
    } catch (err) {
      const definite =
        err instanceof ApiError && err.status >= 400 && err.status < 500;
      if (definite) {
        pending.current = null;
        pendingRemoval.current = null;
        setUncertain(false);
      } else setUncertain(true);
      setError(
        definite
          ? err.message
          : "The save was not confirmed. Check the same save before making another change.",
      );
      if (err instanceof ApiError && err.status === 401) denied.current(err);
      if (err instanceof ApiError && err.status === 409)
        await refresh().catch(() => {});
      return false;
    } finally {
      if (mounted.current) setSaving(false);
    }
  }
  const mutate = (operation: BoardOperation, expectedRevision?: number) => {
    if (!current.current || pending.current) return Promise.resolve(false);
    pendingRemoval.current =
      operation.type === "remove"
        ? (current.current.entries.find(
            (entry) => entry.id === operation.entryId,
          ) ?? null)
        : null;
    return send({
      operationId: crypto.randomUUID(),
      expectedRevision: expectedRevision ?? current.current.revision,
      operation,
    });
  };
  return {
    board,
    connected,
    viewers,
    saving,
    uncertain,
    error,
    notice,
    removedEntry,
    dismissRemoval: () => setRemovedEntry(null),
    setError,
    mutate,
    retry: () =>
      pending.current ? send(pending.current) : Promise.resolve(false),
  };
}

export function RaidPlanner({
  actor,
  enabled,
  onDenied,
  onReview,
}: {
  actor: Actor;
  enabled: boolean;
  onDenied: (error: unknown) => void;
  onReview?: (board: PlanningBoard) => void;
}) {
  const [selected, setSelected] = useState(fromLocation);
  useEffect(() => {
    const read = () => setSelected(fromLocation());
    window.addEventListener("popstate", read);
    return () => window.removeEventListener("popstate", read);
  }, []);
  function navigate(id: string | null) {
    window.history.pushState(null, "", "/raid-planner" + (id ? "/" + id : ""));
    setSelected(id);
    window.scrollTo({ top: 0 });
  }
  if (!enabled)
    return <p className="notice">The raid planner is being prepared.</p>;
  return selected ? (
    <BoardWorkspace
      key={selected}
      id={selected}
      actor={actor}
      onDenied={onDenied}
      onBack={() => navigate(null)}
      onOpen={navigate}
      onReview={onReview}
    />
  ) : (
    <BoardLibrary onOpen={navigate} onDenied={onDenied} />
  );
}

function NewBoard({
  source,
  close,
  onOpen,
}: {
  source?: PlanningBoard | BoardSummary;
  close: () => void;
  onOpen: (id: string) => void;
}) {
  const [title, setTitle] = useState(source ? source.title + " | Copy" : ""),
    [profile, setProfile] = useState<CreateBoard["profile"]>(
      source?.profile ?? "forever",
    ),
    [size, setSize] = useState(source?.size ?? 40),
    [kind, setKind] = useState<CreateBoard["kind"]>("raid"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const attempt = useRef<CreateBoard | null>(null);
  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    // Keep the exact creation request after an uncertain network result.
    const input = attempt.current ?? {
      id: crypto.randomUUID(),
      title,
      profile,
      size,
      kind,
      ...(source
        ? { source: { id: source.id, revision: source.revision } }
        : {}),
    };
    attempt.current = input;
    try {
      const board = await api<PlanningBoard>(endpoint, {
        method: "POST",
        body: JSON.stringify(input),
        signal: AbortSignal.timeout(15_000),
      });
      onOpen(board.id);
    } catch (err) {
      if (err instanceof ApiError && err.status < 500) attempt.current = null;
      setError(
        err instanceof Error
          ? err.message
          : "Creation was not confirmed. Retry to check the same board.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      className="planner-form planner-create"
      onSubmit={(event) => void create(event)}
    >
      <div className="section-heading">
        <h2>{source ? "Make a copy" : "New board"}</h2>
        <button className="text-button" type="button" onClick={close}>
          Cancel
        </button>
      </div>
      <p>
        {source
          ? "Copying saved revision " +
            source.revision +
            ". The new board starts unlocked."
          : "Current guild members can edit this board."}
      </p>
      <fieldset disabled={busy || Boolean(attempt.current)}>
        <label>
          Board name
          <input
            autoFocus
            required
            maxLength={100}
            placeholder="Friday raid | First lineup"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <div className="planner-form-row">
          <label>
            Game profile
            <select
              disabled={Boolean(source)}
              value={profile}
              onChange={(e) =>
                setProfile(e.target.value as CreateBoard["profile"])
              }
            >
              <option value="forever">WoW Forever</option>
              <option value="classic">Original Classic</option>
            </select>
          </label>
          <label>
            Raid size
            <select
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
            >
              {[5, 10, 15, 20, 25, 30, 35, 40].map((n) => (
                <option key={n} value={n}>
                  {n} players
                </option>
              ))}
            </select>
          </label>
          <label>
            Save as
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as CreateBoard["kind"])}
            >
              <option value="raid">Raid plan</option>
              <option value="template">Reusable template</option>
            </select>
          </label>
        </div>
      </fieldset>
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      <button className="button primary" disabled={busy || !title.trim()}>
        {busy
          ? "Creating…"
          : attempt.current
            ? "Check creation"
            : source
              ? "Create copy"
              : "Create board"}
      </button>
    </form>
  );
}

function BoardLibrary({
  onOpen,
  onDenied,
}: {
  onOpen: (id: string) => void;
  onDenied: (error: unknown) => void;
}) {
  const [boards, setBoards] = useState<BoardSummary[] | null>(null),
    [filter, setFilter] = useState("all"),
    [query, setQuery] = useState(""),
    [error, setError] = useState(""),
    [creating, setCreating] = useState(false),
    [source, setSource] = useState<BoardSummary>();
  const load = useCallback(
    () =>
      api<BoardSummary[]>(endpoint)
        .then(setBoards)
        .catch((err: unknown) => {
          if (err instanceof ApiError && [401, 403].includes(err.status))
            onDenied(err);
          else
            setError(err instanceof Error ? err.message : "Boards unavailable");
        }),
    [onDenied],
  );
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [load]);
  const shown =
    boards?.filter(
      (board) =>
        (filter === "all" || board.kind === filter) &&
        board.title.toLowerCase().includes(query.toLowerCase()),
    ) ?? [];
  return (
    <section className="planner-library">
      <div className="planner-intro">
        <div>
          <p className="eyebrow">SHARED PLANNING</p>
          <h2>Raid plans</h2>
          <p>Build a lineup or reuse a saved template.</p>
        </div>
        <button
          className="button primary"
          onClick={() => {
            setSource(undefined);
            setCreating(true);
          }}
        >
          New board
        </button>
      </div>
      {creating && (
        <NewBoard
          key={source?.id ?? "blank"}
          source={source}
          close={() => setCreating(false)}
          onOpen={onOpen}
        />
      )}
      <div className="planner-library-tools">
        <label>
          Find a board
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search plans and templates"
          />
        </label>
        <label>
          Show
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All boards</option>
            <option value="raid">Raid plans</option>
            <option value="template">Templates</option>
          </select>
        </label>
        <button className="button subtle" onClick={() => void load()}>
          Refresh boards
        </button>
      </div>
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {boards === null ? (
        <p role="status">Loading saved boards…</p>
      ) : !shown.length ? (
        <div className="empty">
          <h3>{boards.length ? "No matching boards" : "No boards yet"}</h3>
          <p>
            {boards.length
              ? "Try another name or filter."
              : "Create a board, add placeholders and share its link."}
          </p>
        </div>
      ) : (
        <div className="planner-library-grid">
          {shown.map((board) => (
            <article className="planner-board-card" key={board.id}>
              <div className="card-top">
                <span className="badge gold">
                  {board.kind === "template" ? "Template" : "Raid plan"}
                </span>
                <span className="badge">
                  {board.locked ? "Locked" : "Shared draft"}
                </span>
              </div>
              <button
                className="planner-title-button"
                onClick={() => onOpen(board.id)}
              >
                <h3>{board.title}</h3>
              </button>
              <p>
                {profileName(board.profile)} · {board.size} slots
              </p>
              <div
                className="planner-mini-groups"
                aria-label={
                  board.placed + " placed, " + board.bench + " on bench"
                }
              >
                {Array.from({ length: board.size / 5 }, (_, group) => (
                  <span key={group}>
                    {Array.from({ length: 5 }, (_, slot) => (
                      <i
                        key={slot}
                        className={
                          board.occupiedSlots?.includes(group * 5 + slot)
                            ? "filled"
                            : ""
                        }
                      />
                    ))}
                  </span>
                ))}
              </div>
              <p className="fine">
                {board.placed}/{board.size} placed · {board.bench} on bench
              </p>
              <p className="fine">
                r{board.revision} · {board.updatedBy.displayName}
                <br />
                {when(board.updatedAt)}
              </p>
              <div className="planner-actions">
                <button className="button" onClick={() => onOpen(board.id)}>
                  Open board
                </button>
                <button
                  className="text-button"
                  onClick={() => {
                    setSource(board);
                    setCreating(true);
                    window.scrollTo({ top: 0 });
                  }}
                >
                  Make a copy
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
      <p className="fine planner-footnote">
        Plans remain available when the runner is offline. Names are unverified
        placeholders.
      </p>
    </section>
  );
}

function EntryForm({
  board,
  entry,
  slot,
  busy,
  save,
  close,
}: {
  board: PlanningBoard;
  entry?: PlanningEntry;
  slot: number | null;
  busy: boolean;
  save: (op: BoardOperation, revision: number) => Promise<boolean>;
  close: () => void;
}) {
  const [className, setClassName] = useState(entry?.className ?? "Warrior"),
    [spec, setSpec] = useState(entry?.spec ?? "Arms"),
    [name, setName] = useState(entry?.name ?? ""),
    [role, setRole] = useState<PlanningEntry["role"]>(
      entry?.role ?? "unspecified",
    ),
    [note, setNote] = useState(entry?.note ?? "");
  const [revision] = useState(board.revision),
    [entryId] = useState(entry?.id ?? crypto.randomUUID());
  const options = plannerClasses.find((item) => item.name === className)!;
  return (
    <form
      className="planner-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save(
          {
            type: entry ? "edit" : "add",
            entry: {
              id: entryId,
              name: name.trim() || spec + " " + className,
              className,
              spec,
              role,
              note,
              slot,
            },
          },
          revision,
        ).then((saved) => {
          if (saved) close();
        });
      }}
    >
      <div className="section-heading">
        <h3>
          {entry
            ? "Edit planning entry"
            : slot === null
              ? "Add to bench"
              : "Add to group " + (Math.floor(slot / 5) + 1)}
        </h3>
        <button className="text-button" type="button" onClick={close}>
          Close
        </button>
      </div>
      <fieldset disabled={busy || board.locked}>
        <div className="planner-form-row">
          <label>
            Class
            <select
              value={className}
              onChange={(e) => {
                const next = plannerClasses.find(
                  (item) => item.name === e.target.value,
                )!;
                setClassName(next.name);
                setSpec(next.specs[0]!);
              }}
            >
              {plannerClasses.map((item) => (
                <option key={item.name}>{item.name}</option>
              ))}
            </select>
          </label>
          <label>
            Talent tree
            <select value={spec} onChange={(e) => setSpec(e.target.value)}>
              {options.specs.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Planning name
          <input
            autoFocus
            maxLength={40}
            placeholder={spec + " " + className}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Planned role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as PlanningEntry["role"])}
          >
            {planningRoles.map((value) => (
              <option key={value} value={value}>
                {roleLabels[value]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Short note
          <input
            maxLength={160}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional planning note"
          />
        </label>
      </fieldset>
      <p className="fine">
        Placeholder only. Character ownership and attendance are unverified.
      </p>
      {board.revision !== revision && (
        <p className="notice">
          The board changed. Reopen this form to edit the latest version.
        </p>
      )}
      <button
        className="button primary"
        disabled={busy || board.locked || board.revision !== revision}
      >
        {entry ? "Save entry" : "Add placeholder"}
      </button>
    </form>
  );
}

function BoardDetails({
  board,
  busy,
  save,
  close,
}: {
  board: PlanningBoard;
  busy: boolean;
  save: (op: BoardOperation, revision: number) => Promise<boolean>;
  close: () => void;
}) {
  const [title, setTitle] = useState(board.title),
    [size, setSize] = useState(board.size),
    [notes, setNotes] = useState(board.notes),
    [revision] = useState(board.revision);
  return (
    <form
      className="planner-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save({ type: "details", title, size, notes }, revision).then(
          (saved) => {
            if (saved) close();
          },
        );
      }}
    >
      <div className="section-heading">
        <h3>Board details</h3>
        <button type="button" className="text-button" onClick={close}>
          Close
        </button>
      </div>
      <fieldset disabled={busy || board.locked}>
        <label>
          Board name
          <input
            required
            maxLength={100}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label>
          Raid size
          <select
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
          >
            {[5, 10, 15, 20, 25, 30, 35, 40].map((n) => (
              <option key={n} value={n}>
                {n} players
              </option>
            ))}
          </select>
        </label>
        <label>
          Planning notes
          <textarea
            rows={4}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </fieldset>
      {size < board.size && (
        <p className="notice">
          Entries outside the smaller raid will move to the bench.
        </p>
      )}
      {board.revision !== revision && (
        <p className="notice">
          The board changed. Reopen these details to use the latest version.
        </p>
      )}
      <button
        className="button primary"
        disabled={busy || board.locked || board.revision !== revision}
      >
        Save details
      </button>
    </form>
  );
}

function BoardWorkspace({
  id,
  actor,
  onDenied,
  onBack,
  onOpen,
  onReview,
}: {
  id: string;
  actor: Actor;
  onDenied: (error: unknown) => void;
  onBack: () => void;
  onOpen: (id: string) => void;
  onReview?: (board: PlanningBoard) => void;
}) {
  const shared = useSharedBoard(id, onDenied),
    { board, connected, saving, uncertain, mutate } = shared;
  const [selected, setSelected] = useState<string | null>(null),
    [selectedSpec, setSelectedSpec] = useState<SpecChoice | null>(null),
    [label, setLabel] = useState<{ id: string; originalName: string } | null>(
      null,
    ),
    [dropTarget, setDropTarget] = useState<number | "bench" | null>(null),
    [form, setForm] = useState<{
      slot: number | null;
      entry?: PlanningEntry;
    } | null>(null),
    [details, setDetails] = useState(false),
    [copy, setCopy] = useState(false),
    [historyOpen, setHistoryOpen] = useState(false),
    [history, setHistory] = useState<HistoryEntry[]>([]),
    [oldest, setOldest] = useState(1),
    [preview, setPreview] = useState<PlanningBoard | null>(null),
    [linkNotice, setLinkNotice] = useState("");
  const editorRef = useRef<HTMLDivElement>(null),
    drag = useRef<PlannerDrag | null>(null);
  useEffect(() => {
    if (form || details)
      editorRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
  }, [form, details]);
  useEffect(() => {
    if (
      selected &&
      board &&
      !board.entries.some((entry) => entry.id === selected)
    )
      setSelected(null);
  }, [board?.revision, selected]);
  useEffect(() => {
    if (
      preview ||
      board?.locked ||
      (label && board && !board.entries.some((entry) => entry.id === label.id))
    ) {
      setLabel(null);
      setSelectedSpec(null);
    }
  }, [preview, board?.revision, label?.id]);
  const readHistory = useCallback(
    async (before?: number) => {
      try {
        const value = await api<{
          entries: HistoryEntry[];
          oldestRevision: number;
        }>(
          endpoint +
            "/" +
            id +
            "/history" +
            (before ? "?before=" + before : ""),
        );
        setHistory((current) =>
          before ? [...current, ...value.entries] : value.entries,
        );
        setOldest(value.oldestRevision);
      } catch (err) {
        shared.setError(
          err instanceof Error ? err.message : "History unavailable",
        );
      }
    },
    [id],
  );
  useEffect(() => {
    if (historyOpen) void readHistory();
  }, [historyOpen, board?.revision, readHistory]);
  if (!board)
    return (
      <section>
        <button className="text-button" onClick={onBack}>
          All boards
        </button>
        <p
          role={shared.error ? "alert" : "status"}
          className={shared.error ? "notice error" : ""}
        >
          {shared.error || "Opening the saved board…"}
        </p>
      </section>
    );
  const shown = preview ?? board,
    editable = connected && !saving && !uncertain && !board.locked && !preview;
  const selectedEntry = board.entries.find((entry) => entry.id === selected);
  const placed = shown.entries.filter((entry) => entry.slot !== null),
    bench = shown.entries.filter((entry) => entry.slot === null);
  const nameEntry = (entry: PlanningEntry) => {
    if (!editable) return;
    setLabel({ id: entry.id, originalName: entry.name });
    setSelected(null);
    setSelectedSpec(null);
    setForm(null);
    setDetails(false);
  };
  const removeEntry = async (entry: PlanningEntry) => {
    if (!editable) return;
    if (await mutate({ type: "remove", entryId: entry.id }, board.revision)) {
      if (selected === entry.id) setSelected(null);
      if (form?.entry?.id === entry.id) setForm(null);
    }
  };
  const undoRemoval = async () => {
    const entry = shared.removedEntry;
    if (!editable || !entry) return;
    const slotAvailable =
      entry.slot !== null &&
      entry.slot < board.size &&
      !board.entries.some((other) => other.slot === entry.slot);
    const slot = slotAvailable ? entry.slot : null;
    if (
      await mutate({ type: "add", entry: { ...entry, slot } }, board.revision)
    )
      setLinkNotice(
        entry.name +
          (entry.slot !== null && slot === null
            ? " restored to the bench because their original slot is unavailable."
            : " restored to the plan."),
      );
  };
  const addSpec = async (choice: SpecChoice, slot: number | null) => {
    if (!editable) return;
    if (slot !== null && board.entries.some((entry) => entry.slot === slot)) {
      shared.setError("Choose an empty slot for a new spec, or use the bench.");
      return;
    }
    const entry: PlanningEntry = {
      id: crypto.randomUUID(),
      name: choice.spec + " " + choice.className,
      ...choice,
      role: "unspecified",
      note: "",
      slot,
    };
    if (await mutate({ type: "add", entry }, board.revision)) nameEntry(entry);
  };
  const selectSlot = (slot: number | null, target?: PlanningEntry) => {
    if (!editable) return;
    if (selectedSpec) {
      void addSpec(selectedSpec, slot);
    } else if (selected) {
      if (selected === target?.id) setSelected(null);
      else
        void mutate({ type: "move", entryId: selected, slot }).then((saved) => {
          if (saved) setSelected(null);
        });
    } else if (target) setSelected(target.id);
    else {
      setForm({ slot });
      setDetails(false);
    }
  };
  const endDrag = () => {
    drag.current = null;
    setDropTarget(null);
  };
  const dragOver = (
    event: React.DragEvent,
    slot: number | null,
    occupied = false,
  ) => {
    if (!editable || !drag.current) return;
    if (occupied && drag.current.type === "spec") {
      event.dataTransfer.dropEffect = "none";
      setDropTarget(null);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect =
      drag.current.type === "spec" ? "copy" : "move";
    setDropTarget(slot ?? "bench");
  };
  const dragLeave = (event: React.DragEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null))
      setDropTarget(null);
  };
  const drop = (event: React.DragEvent, slot: number | null) => {
    event.preventDefault();
    event.stopPropagation();
    const item = drag.current;
    endDrag();
    if (!item || !editable) return;
    if (item.type === "spec") void addSpec(item.choice, slot);
    else
      void mutate(
        { type: "move", entryId: item.id, slot },
        board.revision,
      ).then((saved) => {
        if (saved) setSelected(null);
      });
  };
  const tile = (entry: PlanningEntry) => (
    <EntryTile
      key={entry.id}
      entry={entry}
      editable={editable}
      selected={selected === entry.id}
      naming={label?.id === entry.id && !preview}
      onSelect={() => selectSlot(entry.slot, entry)}
      onRename={() => nameEntry(entry)}
      onRemove={() => void removeEntry(entry)}
      onCancelName={() => setLabel(null)}
      onSaveName={async (name) => {
        if (!editable || !label) return false;
        if (entry.name !== label.originalName) {
          shared.setError(
            "This name changed. Cancel, then reopen rename to edit the latest version.",
          );
          return false;
        }
        if (name === entry.name) return true;
        return mutate(
          { type: "edit", entry: { ...entry, name } },
          board.revision,
        );
      }}
      onDragStart={(event) => {
        drag.current = { type: "entry", id: entry.id };
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", entry.id);
      }}
      onDragEnd={endDrag}
      onDragOver={(event) => dragOver(event, entry.slot, entry.slot !== null)}
      onDragLeave={dragLeave}
      onDrop={(event) => drop(event, entry.slot)}
      dropTarget={entry.slot !== null && dropTarget === entry.slot}
    />
  );
  return (
    <section className="planner-workspace">
      <button className="text-button" onClick={onBack}>
        All boards
      </button>
      <div className="planner-board-heading">
        <div>
          <div className="planner-badges">
            <span className="badge gold">{profileName(board.profile)}</span>
            <span className="badge">
              {board.kind === "template" ? "Template" : "Raid plan"}
            </span>
            <span className="badge">
              {board.locked ? "Locked version" : "Shared draft"}
            </span>
          </div>
          <h1>{board.title}</h1>
        </div>
        <div className="planner-save" role="status">
          <span className={connected ? "dot live" : "dot"} />
          {saving
            ? "Saving…"
            : uncertain
              ? "Save unconfirmed"
              : connected
                ? "Saved · Live"
                : "Reconnecting…"}
          <small>
            r{board.revision} · {shared.viewers}{" "}
            {shared.viewers === 1 ? "person" : "people"} viewing
          </small>
        </div>
      </div>
      <div className="planner-actions planner-toolbar">
        <button
          className="button primary"
          disabled={!editable}
          onClick={() => {
            setForm({ slot: null });
            setDetails(false);
            setLabel(null);
            setSelectedSpec(null);
          }}
        >
          <PlannerGlyph name="plus" /> Add entry
        </button>
        <button
          className="button"
          disabled={!editable}
          onClick={() => {
            setDetails(true);
            setForm(null);
            setLabel(null);
            setSelectedSpec(null);
          }}
        >
          <PlannerGlyph name="edit" /> Board details
        </button>
        <button className="button" onClick={() => setCopy(true)}>
          <PlannerGlyph name="copy" /> Make a copy
        </button>
        <button
          className="button"
          onClick={() => setHistoryOpen(!historyOpen)}
          aria-expanded={historyOpen}
        >
          <PlannerGlyph name="history" /> History
        </button>
        <button
          className="button"
          onClick={() =>
            void navigator.clipboard
              .writeText(window.location.origin + "/raid-planner/" + id)
              .then(() => setLinkNotice("Link copied. Guild sign-in required."))
              .catch(() =>
                setLinkNotice(
                  "Board link: " +
                    window.location.origin +
                    "/raid-planner/" +
                    id,
                ),
              )
          }
        >
          <PlannerGlyph name="link" /> Copy link
        </button>
        <a
          className="button subtle"
          href={endpoint + "/" + id + "/export?revision=" + shown.revision}
        >
          <PlannerGlyph name="download" /> Export JSON
        </a>
        {manager(actor) && (
          <button
            className="button subtle"
            disabled={saving || uncertain || !connected || Boolean(preview)}
            onClick={() => void mutate({ type: "lock", locked: !board.locked })}
          >
            <PlannerGlyph name="lock" />{" "}
            {board.locked ? "Unlock board" : "Lock version"}
          </button>
        )}
        {onReview && (
          <button
            className="button subtle"
            disabled={saving || uncertain || Boolean(preview)}
            onClick={() => onReview(board)}
          >
            Review composition
          </button>
        )}
      </div>
      {linkNotice && (
        <p className="notice" role="status">
          {linkNotice}
        </p>
      )}
      {shared.error && (
        <div className="notice error" role="alert">
          {shared.error}
          {uncertain && (
            <button
              className="button"
              disabled={saving || !connected}
              onClick={() => void shared.retry()}
            >
              Check same save
            </button>
          )}
        </div>
      )}
      <div className="planner-feedback" aria-live="polite">
        {!shared.error && shared.notice}
      </div>
      {copy && (
        <NewBoard source={shown} close={() => setCopy(false)} onOpen={onOpen} />
      )}
      {historyOpen && (
        <section className="planner-history">
          <div className="section-heading">
            <h3>Saved revisions</h3>
            <span className="fine">
              Latest 250 retained. Restoring creates a new revision.
            </span>
          </div>
          <ol>
            {history.map((entry) => (
              <li key={entry.revision}>
                <span className="planner-history-rev">r{entry.revision}</span>
                <div>
                  <strong>{entry.summary}</strong>
                  <small>
                    {entry.actor.displayName} · {when(entry.at)}
                  </small>
                </div>
                <button
                  className="text-button"
                  onClick={() =>
                    void api<PlanningBoard>(
                      endpoint + "/" + id + "?revision=" + entry.revision,
                    )
                      .then(setPreview)
                      .catch((err: unknown) =>
                        shared.setError(
                          err instanceof Error
                            ? err.message
                            : "Revision unavailable",
                        ),
                      )
                  }
                >
                  View
                </button>
              </li>
            ))}
          </ol>
          {history.length > 0 &&
            history[history.length - 1]!.revision > oldest && (
              <button
                className="text-button"
                onClick={() =>
                  void readHistory(history[history.length - 1]!.revision)
                }
              >
                Load older revisions
              </button>
            )}
        </section>
      )}
      {preview && (
        <div className="notice planner-preview-notice">
          <div>
            <strong>Viewing saved revision {preview.revision}</strong>
            <p>
              {preview.title} · {when(preview.updatedAt)}. The live board is at
              r{board.revision}.
            </p>
          </div>
          <button className="button" onClick={() => setPreview(null)}>
            Return to live board
          </button>
          {manager(actor) && preview.revision !== board.revision && (
            <button
              className="button primary"
              disabled={board.locked || saving || uncertain || !connected}
              onClick={() =>
                void mutate({
                  type: "restore",
                  revision: preview.revision,
                }).then((saved) => {
                  if (saved) {
                    setPreview(null);
                    setForm(null);
                    setDetails(false);
                    setSelected(null);
                  }
                })
              }
            >
              Restore this composition
            </button>
          )}
          {board.locked && <p>Unlock the live board before restoring.</p>}
        </div>
      )}
      <div className="planner-counts" aria-label="Composition totals">
        <div>
          <strong>
            {placed.length}
            <span>/{shown.size}</span>
          </strong>
          <small>Raid slots</small>
        </div>
        {planningRoles.map((role) => (
          <div key={role}>
            <strong>
              {placed.filter((entry) => entry.role === role).length}
            </strong>
            <small>{roleLabels[role]}</small>
          </div>
        ))}
        <div>
          <strong>{bench.length}</strong>
          <small>Bench</small>
        </div>
      </div>
      <SpecPalette
        entries={placed}
        disabled={!editable}
        selected={selectedSpec}
        onSelect={(choice) => {
          setSelectedSpec(choice);
          setSelected(null);
          setLabel(null);
          setForm(null);
          setDetails(false);
        }}
        onDragStart={(event, choice) => {
          drag.current = { type: "spec", choice };
          event.dataTransfer.effectAllowed = "copy";
          event.dataTransfer.setData(
            "text/plain",
            choice.spec + " " + choice.className,
          );
        }}
        onDragEnd={endDrag}
      />
      {shown.notes && <p className="planner-board-notes">{shown.notes}</p>}
      <div ref={editorRef}>
        {form && !preview && (
          <EntryForm
            key={(form.entry?.id ?? "new") + ":" + form.slot}
            board={board}
            entry={form.entry}
            slot={form.slot}
            busy={!editable}
            save={mutate}
            close={() => setForm(null)}
          />
        )}
        {details && !preview && (
          <BoardDetails
            board={board}
            busy={!editable}
            save={mutate}
            close={() => setDetails(false)}
          />
        )}
      </div>
      <div className="planner-selection" aria-live="polite">
        {selectedSpec && editable ? (
          <>
            <span
              className="planner-selected-spec"
              data-class={selectedSpec.className}
              data-spec={selectedSpec.spec}
            >
              <SpecIcon {...selectedSpec} />
              <strong>
                {selectedSpec.spec} {selectedSpec.className}
              </strong>
            </span>
            <span>Choose an empty slot or the bench.</span>
            <button
              className="text-button"
              onClick={() => setSelectedSpec(null)}
            >
              Cancel
            </button>
          </>
        ) : selectedEntry && !preview ? (
          <>
            <strong>{selectedEntry.name} selected.</strong>
            <span>Choose a destination to move or swap.</span>
            <button
              className="text-button"
              disabled={!editable}
              onClick={() => {
                setForm({ slot: selectedEntry.slot, entry: selectedEntry });
                setDetails(false);
                setLabel(null);
              }}
            >
              Edit entry
            </button>
            <button
              className="text-button"
              disabled={!editable}
              onClick={() =>
                void mutate({
                  type: "move",
                  entryId: selectedEntry.id,
                  slot: null,
                }).then((saved) => {
                  if (saved) setSelected(null);
                })
              }
            >
              Move to bench
            </button>
            <button
              className="text-button"
              disabled={!editable}
              onClick={() => void removeEntry(selectedEntry)}
            >
              Remove from plan
            </button>
            <button className="text-button" onClick={() => setSelected(null)}>
              Deselect
            </button>
          </>
        ) : (
          <span>
            {preview
              ? "Saved composition | Read only"
              : board.locked
                ? "Board locked. Make a copy to edit."
                : label
                  ? "Enter saves the name. Escape cancels."
                  : "Drag or select to move and swap. Use the pencil to rename or the bin to remove."}
          </span>
        )}
      </div>
      <div className="planner-groups">
        {Array.from({ length: shown.size / 5 }, (_, group) => (
          <section
            className="planner-group"
            key={group}
            aria-label={"Group " + (group + 1)}
          >
            <header>
              <h3>Group {group + 1}</h3>
              <span>
                {
                  placed.filter(
                    (entry) => Math.floor(entry.slot! / 5) === group,
                  ).length
                }
                /5
              </span>
            </header>
            {Array.from({ length: 5 }, (_, row) => {
              const slot = group * 5 + row,
                entry = placed.find((entry) => entry.slot === slot);
              return entry ? (
                tile(entry)
              ) : (
                <button
                  className={
                    "planner-empty-slot" +
                    (dropTarget === slot ? " drop-target" : "") +
                    (selectedSpec ? " choosing-spec" : "")
                  }
                  key={"slot" + slot}
                  disabled={!editable}
                  aria-label={
                    "Group " + (group + 1) + ", slot " + (row + 1) + ", empty"
                  }
                  onClick={() => selectSlot(slot)}
                  onDragOver={(event) => dragOver(event, slot)}
                  onDragLeave={dragLeave}
                  onDrop={(event) => drop(event, slot)}
                >
                  <span>
                    {selected ? "Move here" : selectedSpec ? "Place here" : "+"}
                  </span>
                  <small>
                    {selected || selectedSpec ? "Empty slot" : "Drop a spec"}
                  </small>
                </button>
              );
            })}
          </section>
        ))}
      </div>
      <section
        className={
          "planner-bench" + (dropTarget === "bench" ? " drop-target" : "")
        }
        aria-label="Bench"
        onDragOver={(event) => dragOver(event, null)}
        onDragLeave={dragLeave}
        onDrop={(event) => drop(event, null)}
      >
        <div className="section-heading">
          <h3>
            Bench <span className="fine">{bench.length} entries</span>
          </h3>
          <button
            className="text-button"
            disabled={!editable}
            onClick={() => selectSlot(null)}
          >
            {selected
              ? "Move selected to bench"
              : selectedSpec
                ? "Place spec on bench"
                : "+ Add to bench"}
          </button>
        </div>
        {bench.length ? (
          <div className="planner-bench-grid">{bench.map(tile)}</div>
        ) : (
          <button
            className="planner-bench-empty"
            disabled={!editable}
            onClick={() => selectSlot(null)}
            onDragOver={(event) => dragOver(event, null)}
            onDragLeave={dragLeave}
            onDrop={(event) => drop(event, null)}
          >
            Bench is empty.{" "}
            {selected
              ? "Move selected entry to bench."
              : "Drop an entry or add a placeholder."}
          </button>
        )}
      </section>
      {shared.removedEntry && !preview && (
        <div className="planner-removal" role="status">
          <span>
            <strong>{shared.removedEntry.name}</strong> removed from this plan.
          </span>
          <button
            className="button"
            disabled={!editable}
            onClick={() => void undoRemoval()}
          >
            Undo
          </button>
          <button
            type="button"
            className="planner-icon-button"
            aria-label="Dismiss removal notice"
            onClick={shared.dismissRemoval}
          >
            <PlannerGlyph name="close" />
          </button>
        </div>
      )}
      <footer className="planner-footnote">
        <p>
          Saved {when(board.updatedAt)} by {board.updatedBy.displayName}. All
          current guild members can edit unlocked drafts.
        </p>
        <p>
          Counts exclude the bench. Buff and utility rules for{" "}
          {profileName(shown.profile)} remain unverified. Plans do not confirm
          assignments, attendance or loot priority.
        </p>
      </footer>
    </section>
  );
}
